<template>
  <div
    class="w-full max-w-md mx-auto bg-[#1F1F1F] text-white rounded-2xl shadow-2xl p-6 sm:p-8 relative"
  >
    <!-- Close Button -->
    <button
      class="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl font-bold"
      @click.prevent="$emit('close')"
    >
      ×
    </button>

    <!-- Logo and Heading -->
    <div class="flex items-center gap-2 mb-4">
      <Icon icon="ic:baseline-login" width="24" height="24" />
      <h1 class="font-semibold text-lg text-white">{{ t("login.title") }}</h1>
    </div>

    <p class="text-sm text-gray-400 mb-6">{{ t("login.description") }}</p>

    <!-- Email -->
    <div class="relative mb-4">
      <input
        v-model="localForm.email"
        type="email"
        :placeholder="t('login.email')"
        class="w-full pl-10 pr-4 py-3 rounded-md border border-[#2A2A2A] bg-[#2A2A2A] text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3DCBB1]"
      />
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon icon="mdi:email-outline" class="w-4 h-4" />
      </span>
    </div>

    <!-- Password -->
    <div class="relative mb-6">
      <input
        v-model="localForm.password"
        type="password"
        :placeholder="t('login.password')"
        class="w-full pl-10 pr-4 py-3 rounded-md border border-[#2A2A2A] bg-[#2A2A2A] text-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3DCBB1]"
      />
      <span class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
        <Icon icon="mdi:lock-outline" class="w-4 h-4" />
      </span>
    </div>

    <!-- Giriş -->
    <button
      class="w-full bg-[#3DCBB1] hover:bg-[#32b0a0] text-white font-semibold py-3 rounded-md transition duration-200"
      @click.prevent="login"
    >
      {{ t("login.submit") }}
    </button>

    <div class="text-center text-sm text-gray-500 my-4">
      {{ t("login.or") }}
    </div>

    <!-- Google -->
    <button
      class="w-full flex items-center justify-center gap-2 border border-[#444] text-white bg-[#1F1F1F] hover:bg-[#2A2A2A] font-medium py-2 rounded-md transition"
      @click.prevent="socialLogin('google')"
    >
      <Icon icon="mdi:google" class="w-5 h-5" />
      {{ t("login.google") }}
    </button>

    <!-- Kayıt -->
    <div class="text-center mt-6 text-sm text-gray-400">
      {{ t("login.no_account") }}
      <button
        class="text-[#3DCBB1] hover:underline font-medium"
        @click.prevent="$emit('change')"
      >
        {{ t("login.register") }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { useSupabaseClient, useI18n } from "#imports";
import { watch, reactive } from "vue";

const { t } = useI18n();

const props = defineProps<{
  form: { email: string; password: string };
}>();

const emit = defineEmits(["update:form", "close", "change", "alert"]);

const supabase = useSupabaseClient();

const localForm = reactive({
  email: props.form.email,
  password: props.form.password,
});

watch(localForm, (val) => emit("update:form", { ...val }), { deep: true });

const login = async () => {
  const { error } = await supabase.auth.signInWithPassword({
    email: localForm.email,
    password: localForm.password,
  });

  if (error) {
    const msg = error.message.toLowerCase();

    if (msg.includes("email not confirmed")) {
      const { error: resendError } = await supabase.auth.resend({
        type: "signup",
        email: localForm.email,
      });

      if (resendError) {
        emit("alert", {
          message: t("errors.email_resend_failed"),
          icon: "mdi:email-off",
          type: "error",
        });
      } else {
        emit("alert", {
          message: t("errors.email_verification_sent"),
          icon: "mdi:email-send",
          type: "warning",
        });
      }
      return;
    }

    let translated = t("errors.login_failed");
    if (msg.includes("invalid login credentials"))
      translated = t("errors.invalid_credentials");
    else if (msg.includes("user not found"))
      translated = t("errors.user_not_found");
    else if (msg.includes("invalid email"))
      translated = t("errors.invalid_email");

    emit("alert", {
      message: translated,
      icon: "mdi:alert-circle",
      type: "error",
    });

    return;
  }

  emit("alert", {
    message: t("alerts.login_success"),
    icon: "mdi:check-circle",
    type: "success",
  });

  emit("close");
  navigateTo("/");
};

const socialLogin = async (provider: "google") => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: window.location.origin,
    },
  });

  if (error) {
    emit("alert", {
      message: t("errors.google_login_failed"),
      icon: "mdi:alert-circle",
      type: "error",
    });
  }
};
</script>
